import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const SETUP_KEY = 'TYRESTORE_OWNER_2024';

const AdminRegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    setupKey: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check setup key
    if (formData.setupKey !== SETUP_KEY) {
      return setError('Invalid setup key. Access denied.');
    }

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match.');
    }

    if (formData.password.length < 8) {
      return setError('Password must be at least 8 characters.');
    }

    setLoading(true);
    try {
      const { data } = await API.post('/auth/admin-register', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        setupKey: formData.setupKey
      });
      loginUser(data);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgCircle1} />
      <div style={styles.bgCircle2} />

      <div style={styles.card}>
        <div style={styles.iconRow}>
          <span style={styles.lockIcon}>🛡️</span>
        </div>

        <h2 style={styles.title}>Owner Setup</h2>
        <p style={styles.subtitle}>Create an owner account — max 2 owners allowed</p>

        {error && (
          <div style={styles.errorBox}>⚠️ {error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Owner Name</label>
            <input
              style={styles.input}
              type="text"
              name="name"
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input
              style={styles.input}
              type="email"
              name="email"
              placeholder="owner@tyrestore.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              name="password"
              placeholder="Min 8 characters"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <input
              style={styles.input}
              type="password"
              name="confirmPassword"
              placeholder="Repeat password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Secret Setup Key</label>
            <input
              style={styles.input}
              type="password"
              name="setupKey"
              placeholder="Enter setup key"
              value={formData.setupKey}
              onChange={handleChange}
              required
            />
          </div>

          <button
            style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
            type="submit"
            disabled={loading}
          >
            {loading ? 'Creating...' : '🛡️ Create Owner Account'}
          </button>
        </form>

        <p style={styles.hint}>
          Already have an account?{' '}
          <span style={styles.hintLink} onClick={() => navigate('/admin-login')}>
            Login here
          </span>
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    padding: '20px'
  },
  bgCircle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(230,57,70,0.15) 0%, transparent 70%)',
    top: '-100px',
    right: '-100px'
  },
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(230,57,70,0.1) 0%, transparent 70%)',
    bottom: '-80px',
    left: '-80px'
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(20px)',
    padding: '48px 40px',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
    position: 'relative',
    zIndex: 1
  },
  iconRow: { textAlign: 'center', marginBottom: '20px' },
  lockIcon: { fontSize: '48px' },
  title: { color: '#fff', fontSize: '26px', fontWeight: 'bold', textAlign: 'center', marginBottom: '6px' },
  subtitle: { color: '#aaa', fontSize: '13px', textAlign: 'center', marginBottom: '28px' },
  errorBox: { background: 'rgba(230,57,70,0.15)', color: '#ff6b6b', border: '1px solid rgba(230,57,70,0.3)', borderRadius: '10px', padding: '12px 16px', fontSize: '14px', marginBottom: '20px' },
  field: { marginBottom: '18px' },
  label: { display: 'block', color: '#ccc', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  input: { width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  button: { width: '100%', padding: '14px', background: '#e63946', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginTop: '8px', boxShadow: '0 4px 20px rgba(230,57,70,0.4)' },
  hint: { color: '#666', textAlign: 'center', marginTop: '24px', fontSize: '13px' },
  hintLink: { color: '#e63946', cursor: 'pointer', fontWeight: '600' }
};

export default AdminRegisterPage;